import { Toaster } from "@/components/Ui/toaster"
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from './api/query-client'
import React, { Suspense } from 'react';
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';
import PageNotFound from './api/PageNotFound';
import { AuthProvider, useAuth } from './api/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError'; // No change needed, file created
import RouteGuard from '@/components/Auth/RouteGuard';
import { CONSOLE_MODES, consolePageMode } from '@/utils/consoleAccess';
import { StagingRoomMount } from '@/components/Landing/AccessGateway';
import PostAuthSplashGate from '@/components/System/PostAuthSplashGate';
import { canAccessCameraConsole } from '@/features/camera/access/cameraConsoleAccess.js';
import OperationalPresenceProvider from '@/features/operationalPresence/console/OperationalPresenceProvider';
// Add page imports here

const SimulationManager = React.lazy(() => import('./pages/SimulationManager/SimulationManager'));
const IncidentCommander = React.lazy(() => import('./pages/IncidentCommander/IncidentCommander'));
const MetricsPanel = React.lazy(() => import('./pages/MetricsPanel/MetricsPanel'));
const Logistics = React.lazy(() => import('./pages/Logistics/Logistics'));
const Finance = React.lazy(() => import('./pages/Finance/Finance'));
const Operations = React.lazy(() => import('./pages/Operations/Operations'));
const OperationCommander = React.lazy(() => import('./pages/OperationCommander/OperationCommander'));
const Safety = React.lazy(() => import('./pages/Safety/Safety'));
const TransmissionPanel = React.lazy(() => import('./pages/TransmissionPanel/TransmissionPanel'));
const Communications = React.lazy(() => import('./pages/Communications'));
const Liaison = React.lazy(() => import('./pages/Liaison'));
const PressRoom = React.lazy(() => import('./pages/PressRoom/PressRoom'));
const AdminConsole = React.lazy(() => import('./pages/AdminConsole/AdminConsole'));
const MetricsResourcesDashboard = React.lazy(() => import('./pages/MetricsResourcesDashboard/MetricsResourcesDashboard'));
const DisplayMode = React.lazy(() => import('./pages/DisplayMode/DisplayMode'));
const ParticipantNavigator = React.lazy(() => import('./pages/ParticipantNavigator/ParticipantNavigator'));
const SoundControlRoom = React.lazy(() => import('./pages/SoundControlRoom/SoundControlRoom'));
const Landing = React.lazy(() => import('./pages/Landing/Landing'));
const Table = React.lazy(() => import('./pages/Table/Table'));
const Auth = React.lazy(() => import('./pages/Auth/Auth'));
const TransmissionView = React.lazy(() => import('./pages/TransmissionView/TransmissionView'));
const Holodome = React.lazy(() => import('./pages/Holodome/Holodome'));
const HolodomeStage = React.lazy(() => import('./pages/HolodomeStage'));
const CentralSimulationRoom = React.lazy(() => import('./pages/CentralSimulationRoom/CentralSimulationRoom'));
const GodMode = React.lazy(() => import('./pages/GodMode/GodMode'));
const AcademiaReacta = React.lazy(() => import('./pages/AcademiaReacta/AcademiaReacta'));
const ChemicalDatabase = React.lazy(() => import('./pages/chemicalDatabase/chemicalDatabase'));
const AccountManager = React.lazy(() => import('./pages/AccountManager/AccountManager'));
const Library = React.lazy(() => import('./pages/Library/Library'));
const IntegratorConsole = React.lazy(() => import('./pages/IntegratorConsole'));
const MedicalServices = React.lazy(() => import('./pages/MedicalServices'));
const SituationalIntelligenceCenter = React.lazy(() => import('./pages/SituationalIntelligenceCenter'));
const IntelligenceOfficer = React.lazy(() => import('./pages/IntelligenceOfficer'));
const CameraOperationsConsoleWindow = React.lazy(() => import('./pages/CameraOperationsConsoleWindow'));
const CameraDiscoveryWindow = React.lazy(() => import('./pages/CameraDiscoveryWindow'));
const FieldApp = React.lazy(() => import('./features/field/FieldApp'));

function RouteLoading() {
  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black text-cyan-200">
      <div className="w-8 h-8 border-4 border-cyan-900 border-t-cyan-300 rounded-full animate-spin" />
    </div>
  );
}

class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error) {
    console.error('[REACTA ROUTE ERROR]', error);
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black px-6 text-cyan-100">
        <div className="max-w-xl rounded-lg border border-cyan-400/40 bg-slate-950 p-6 shadow-2xl shadow-cyan-950/40">
          <h1 className="text-lg font-bold uppercase tracking-wider text-cyan-200">Falha ao carregar módulo</h1>
          <p className="mt-3 text-sm text-slate-300">
            O REACTA não conseguiu carregar os arquivos desta rota. Atualize a página para sincronizar a versão publicada.
          </p>
        </div>
      </div>
    );
  }
}

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();

  // Show loading spinner while checking app public settings or auth
  if (isLoadingPublicSettings || isLoadingAuth) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black text-cyan-100">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-4 border-cyan-900 border-t-cyan-300 rounded-full animate-spin"></div>
          <div className="text-xs font-semibold uppercase tracking-wider text-cyan-300">Validando sessão REACTA</div>
        </div>
      </div>
    );
  }

  // Handle authentication errors
  if (authError) {
    if (authError.type === 'user_not_registered') {
      return <UserNotRegisteredError />;
    } else if (authError.type === 'auth_required') {
      // Redirect to the current public landing, avoiding the legacy /Auth screen.
      if (window.location.pathname !== '/Landing' && window.location.pathname !== '/') {
        navigateToLogin();
      }
      if (window.location.pathname !== '/Landing' && window.location.pathname !== '/') return null;
    }
  }

  // Render the main app
  //
  // OperationalPresenceProvider envolve o bloco de rotas abaixo (nao fica
  // dentro de nenhuma Route individual) de proposito: e o UNICO ponto da
  // arvore que sobrevive a troca de rota entre consoles (Mapa Tatico,
  // Logistica, Seguranca...). Cada pagina monta seu proprio Layout, que
  // remonta a cada navegacao — se a Presenca Operacional morasse dentro do
  // Layout (como antes desta missao), a Room LiveKit seria destruida e
  // recriada a cada troca de console. Ver comentario de topo em
  // OperationalPresenceProvider.jsx.
  return (
    <AppErrorBoundary>
      <OperationalPresenceProvider>
      <Suspense fallback={<RouteLoading />}>
      <Routes>
        <Route path="/SimulationManager" element={<RouteGuard><SimulationManager /></RouteGuard>} />
        <Route path="/IncidentCommander" element={<RouteGuard><IncidentCommander /></RouteGuard>} />
        <Route path="/MetricsPanel" element={<MetricsPanel />} />
        <Route path="/Logistics" element={<Logistics />} />
        <Route path="/Finance" element={<Finance />} />
        <Route path="/Operations" element={<Operations />} />
        <Route path="/OperationsCommander" element={<RouteGuard><OperationCommander /></RouteGuard>} />
        <Route path="/OperationCommander" element={<RouteGuard><OperationCommander /></RouteGuard>} />
        <Route path="/Safety" element={<Safety />} />
        <Route path="/TransmissionPanel" element={<TransmissionPanel />} />
        <Route path="/Communications" element={<Communications />} />
        <Route path="/Liaison" element={<Liaison />} />
        <Route path="/PressRoom" element={<PressRoom />} />
        <Route path="/AdminConsole" element={<RouteGuard><AdminConsole /></RouteGuard>} />
        <Route path="/MetricsResourcesDashboard" element={<MetricsResourcesDashboard />} />
        <Route path="/DisplayMode" element={<DisplayMode />} />
        <Route path="/ParticipantNavigator" element={<ParticipantNavigator />} />
        <Route path="/SoundControlRoom" element={<SoundControlRoom />} />
        <Route path="/SoundAPIConfig" element={<SoundControlRoom />} />
        <Route path="/" element={<Landing />} />
        <Route path="/Landing" element={<Landing />} />
        <Route path="/Table" element={<Table />} />
        <Route path="/Auth" element={<Auth />} />
        <Route path="/TransmissionView" element={<TransmissionView />} />
        <Route path="/Holodome" element={<Holodome />} />
        <Route path="/HolodomeStage" element={<HolodomeStage />} />
        <Route path="/CentralSimulationRoom" element={<CentralSimulationRoom />} />
        <Route path="/GodMode" element={<GodMode />} />
        <Route path="/AcademiaReacta" element={<AcademiaReacta />} />
        <Route path="/ChemicalDatabase" element={<ChemicalDatabase />} />
        <Route path="/AccountManager" element={<AccountManager />} />
        <Route path="/Library" element={<Library />} />
        <Route path="/IntegratorConsole" element={<IntegratorConsole />} />
        <Route path="/MedicalServices" element={<MedicalServices />} />
        <Route
          path="/SituationalIntelligenceCenter"
          element={<SituationalIntelligenceCenter />}
        />
        <Route
          path="/IntelligenceOfficer"
          element={<IntelligenceOfficer />}
        />
        <Route
          path="/CameraOperationsConsole"
          element={<RouteGuard accessCheck={canAccessCameraConsole}><CameraOperationsConsoleWindow /></RouteGuard>}
        />
        <Route path="/CameraDiscovery" element={<RouteGuard accessCheck={(user) => consolePageMode(user, 'CameraDiscovery') !== CONSOLE_MODES.DENIED}><CameraDiscoveryWindow /></RouteGuard>} />
        {/* Add your page Route elements here */}
        <Route path="*" element={<PageNotFound />} />
      </Routes>
      </Suspense>
      </OperationalPresenceProvider>
    </AppErrorBoundary>
  );
};


function DesktopApp() {

  return (
    <AuthProvider>
      {/* Global mount for Staging Room (mock flow) */}
      <StagingRoomMount />
      <PostAuthSplashGate />
      <QueryClientProvider client={queryClientInstance}>
        <AuthenticatedApp />
        <Toaster />
      </QueryClientProvider>
    </AuthProvider>
  )
}

// REACTA FIELD (ADR-004): /field/* fica no mesmo roteador, mas FORA do
// AuthProvider e do OperationalPresenceProvider. No modo inativo o FIELD nao
// consulta /api/auth/me, nao conecta o realtime e nao monta presenca; e o
// redirecionamento de "auth_required" do desktop nao o alcanca. Todas as
// demais rotas seguem exatamente a arvore anterior (DesktopApp).
function App() {

  return (
    <Router>
      <Routes>
        <Route
          path="/field/*"
          element={(
            <AppErrorBoundary>
              <Suspense fallback={<RouteLoading />}>
                <FieldApp />
              </Suspense>
            </AppErrorBoundary>
          )}
        />
        <Route path="*" element={<DesktopApp />} />
      </Routes>
    </Router>
  )
}

export default App
